/**
 * ExcelViewer Component
 *
 * Displays modern Excel files with sheet navigation,
 * sorting, filtering, and pagination using react-table.
 */

import { useState, useEffect, useMemo } from 'react';
import readXlsxFile from 'read-excel-file/browser';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { Loader2, AlertCircle, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

interface ExcelViewerProps {
  /** Path to the Excel file */
  path: string;
  /** File name (for display) */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

type RowData = Record<string, string | number | boolean | null>;
type SpreadsheetCell = string | number | boolean | Date | null;
type SpreadsheetSheet = {
  sheet: string;
  data: SpreadsheetCell[][];
};

function formatCellValue(value: SpreadsheetCell | undefined): string | number | boolean | null {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value.toISOString();
  return value;
}

function buildColumnNames(headerRow: SpreadsheetCell[]): string[] {
  const seen = new Map<string, number>();
  return headerRow.map((cell, index) => {
    const base = String(formatCellValue(cell) ?? '').trim() || `Column ${index + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base} ${count + 1}`;
  });
}

function rowToData(columns: string[], row: SpreadsheetCell[]): RowData {
  return columns.reduce<RowData>((result, column, index) => {
    result[column] = formatCellValue(row[index]);
    return result;
  }, {});
}

// ============================================
// ExcelViewer Component
// ============================================

export function ExcelViewer({ path, filename, className }: ExcelViewerProps) {
  const [data, setData] = useState<RowData[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [sheets, setSheets] = useState<SpreadsheetSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Load workbook on mount
  useEffect(() => {
    async function loadWorkbook() {
      setLoading(true);
      setError(null);

      try {
        // Download file via API
        const response = await api.files.download(path);
        const arrayBuffer = await response.arrayBuffer();

        // Parse Excel file
        const parsedSheets = await readXlsxFile(arrayBuffer) as SpreadsheetSheet[];
        setSheets(parsedSheets);
        setSheetNames(parsedSheets.map((sheet) => sheet.sheet));

        // Load first sheet by default
        if (parsedSheets.length > 0) {
          setActiveSheet(parsedSheets[0].sheet);
        }
      } catch (err) {
        console.error('Failed to load Excel file:', err);
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setLoading(false);
      }
    }

    loadWorkbook();
  }, [path]);

  // Load sheet data when active sheet changes
  useEffect(() => {
    if (sheets.length === 0 || !activeSheet) return;

    const sheet = sheets.find((item) => item.sheet === activeSheet);
    if (!sheet) return;

    const [headerRow, ...bodyRows] = sheet.data;
    if (headerRow && headerRow.length > 0) {
      const cols = buildColumnNames(headerRow);
      setColumns(cols);
      setData(bodyRows.map((row) => rowToData(cols, row)));
    } else {
      setColumns([]);
      setData([]);
    }
  }, [sheets, activeSheet]);

  // Create table columns
  const tableColumns = useMemo<ColumnDef<RowData>[]>(
    () =>
      columns.map((col) => ({
        accessorKey: col,
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-primary-400 transition-colors"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            {col}
            <ArrowUpDown className="w-3 h-3" />
          </button>
        ),
        cell: ({ getValue }) => {
          const value = getValue();
          if (value === null || value === undefined) return '';
          return String(value);
        },
      })),
    [columns]
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 50 },
    },
  });

  if (loading) {
    return (
      <div className={cn('h-full flex items-center justify-center bg-surface-900', className)}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-surface-400">Loading {filename}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('h-full flex items-center justify-center bg-surface-900', className)}>
        <div className="text-center text-destructive-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-2" />
          <div className="font-medium">Error loading file</div>
          <div className="text-sm mt-1 text-surface-400">{error}</div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('h-full flex items-center justify-center bg-surface-900', className)}>
        <div className="text-center text-surface-400">
          <div className="font-medium">Empty spreadsheet</div>
          <div className="text-sm mt-1">No data found in this sheet</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col bg-surface-900', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-surface-700 bg-surface-800">
        {/* Sheet selector */}
        {sheetNames.length > 1 && (
          <select
            value={activeSheet}
            onChange={(e) => setActiveSheet(e.target.value)}
            className="h-8 px-2 text-xs bg-surface-700 border border-surface-600 rounded text-surface-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {sheetNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}

        {/* Search */}
        <input
          type="text"
          placeholder="Search..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-surface-600 rounded bg-surface-700 text-surface-200 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        <div className="flex-1" />

        {/* Row count */}
        <span className="text-xs text-surface-400">
          {table.getFilteredRowModel().rows.length} rows
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface-800 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-2 text-left font-medium text-surface-300 border-b border-surface-700 whitespace-nowrap"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <tr
                key={row.id}
                className={cn(
                  i % 2 === 0 ? 'bg-surface-900' : 'bg-surface-850',
                  'hover:bg-surface-800 transition-colors'
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-3 py-2 text-surface-300 border-b border-surface-800 whitespace-nowrap"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-surface-700 bg-surface-800">
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-400">Rows per page:</span>
          <select
            value={String(table.getState().pagination.pageSize)}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="h-7 px-2 text-xs bg-surface-700 border border-surface-600 rounded text-surface-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {[25, 50, 100, 250].map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-400">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-7 w-7 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-7 w-7 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ExcelViewer;
